import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import api, { errMsg } from "@/lib/api";

export function DailySheetImport({ plantId, reportingDate }) {
  const ref = useRef();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const template = async () => {
    try {
      const res = await api.get("/records/daily/template", { params: { plant_id: plantId, reporting_date: reportingDate }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename=([^;]+)/)?.[1] || `DailyTemplate_${reportingDate}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill Plan / Produced / Defects / FPY / OEE and upload");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/records/daily/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data) return;
      setResult(data);
      toast[data.errors.length ? "warning" : "success"](`${data.created} row(s) locked · ${data.skipped} blank skipped · ${data.errors.length} error(s)`);
      qc.invalidateQueries({ queryKey: ["day-sheet"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); qc.invalidateQueries({ queryKey: ["records"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div className="pp-card p-4 border-blue-200 bg-blue-50/40" data-testid="daily-import">
      <div className="flex flex-wrap items-center gap-3">
        <FileSpreadsheet size={20} className="text-blue-600 shrink-0" />
        <div className="text-sm min-w-0 flex-1">
          <div className="font-semibold">Daily Excel / CSV upload</div>
          <div className="text-xs text-slate-500">Columns: Date, Line Code, FG Code / Model, Plan Qty, Produced Qty, Defect Qty, FPY %, OEE %, Downtime min, Downtime Category, Defect Reason, Remarks. Template is pre-filled with one row per line × model (already-locked rows excluded). Rows with blank Produced Qty are skipped; each filled row locks. Undo the whole upload from Upload History.</div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" className="pp-btn-ghost h-9" onClick={template} disabled={!plantId} data-testid="daily-template-btn"><Download size={14} /> Template</button>
          <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="daily-import-input" />
          <button type="button" className="pp-btn-primary h-9" disabled={busy} onClick={() => ref.current?.click()} data-testid="daily-import-btn"><Upload size={14} /> {busy ? "Uploading…" : "Upload Excel / CSV"}</button>
        </div>
      </div>
      {result && (
        <div className="mt-3 text-xs font-mono text-slate-700" data-testid="daily-import-result">
          {result.rows} rows · <span className="text-emerald-700">{result.created} locked</span> · <span className="text-slate-500">{result.skipped} skipped (blank)</span> · <span className={result.errors.length ? "text-rose-700" : ""}>{result.errors.length} errors</span>
          <div className="max-h-40 overflow-y-auto mt-1">{result.errors.map((e, i) => <div key={i} className="text-rose-700">Row {e.row} ({e.key}): {e.error}</div>)}</div>
        </div>
      )}
    </div>
  );
}
